import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { ArrowLeft } from 'lucide-react';

const FormViewer: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const businessKey = searchParams.get('business_key');
  const navigate = useNavigate();
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFormPreview = async () => {
      if (!projectId || !businessKey) {
        setError('Project ID or Business Key is missing');
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/projects/${projectId}/dataform-agent/form-preview/${encodeURIComponent(businessKey)}`);
        if (response.data.html_content) {
          setHtmlContent(response.data.html_content);
        } else {
          setError('No form preview available');
        }
      } catch (err: any) {
        console.error('Error fetching form preview:', err);
        setError(err.response?.data?.detail || 'Failed to load form preview');
      } finally {
        setLoading(false);
      }
    };

    fetchFormPreview();
  }, [projectId, businessKey]);

  const handleBack = () => {
    navigate(`/fedramp/chat/${projectId}`);
  };

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading form preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Project
          </button>
        </div>
      </div>
    );
  }

  if (!htmlContent) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-gray-600 mb-4">No form preview available</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Project
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-white">
      <iframe
        srcDoc={htmlContent}
        className="w-full h-full border-0"
        title="Form Preview"
        sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-storage-access-by-user-activation"
      />
    </div>
  );
};

export default FormViewer;

